import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { PricingTierVersion, PricingTierVersionItem } from '../Models/PricingTierVersion';

interface RenewItem {
  serviceTypeId: number;
  pricingType: string;
  cost: number | null;
}

@Component({
  selector: 'app-admin-pricing',
  templateUrl: './admin-pricing.component.html',
  styleUrls: ['./admin-pricing.component.css'],
  standalone: false
})
export class AdminPricingComponent implements OnInit {

  allVersions: PricingTierVersion[] = [];
  tierNames: string[] = [];

  selectedTierName: string = '';
  versionsForTier: PricingTierVersion[] = [];
  selectedVersionId: number | null = null;
  selectedVersion: PricingTierVersion | null = null;

  currentItems: PricingTierVersionItem[] = [];
  loadingItems = false;

  isRenewing = false;
  renewStartDate: Date = new Date();
  renewItems: RenewItem[] = [];
  saving = false;

  readonly serviceTypeLabels: Record<number, string> = { 1: 'Named', 2: 'Discretionary', 3: 'Trust' };

  readonly pricingTypeLabels: Record<string, string> = {
    MonthlySub: 'Monthly Subscription',
    NotificationCost: 'Notification Cost',
    EandWAnnualSub: 'England & Wales Annual Subscription',
    SixRegionSub: 'Six Region Subscription',
    AdditionalRegion: 'Additional Region',
    AdditionalCategory: 'Additional Category'
  };

  constructor(private http: HttpClient, private toastr: ToastrService) {}

  async ngOnInit() {
    try {
      this.allVersions = await lastValueFrom(
        this.http.get<PricingTierVersion[]>('/charitycentral/GetAllPricingTierVersions')
      );
      const seen = new Set<string>();
      this.tierNames = this.allVersions
        .map(v => v.name)
        .filter(n => { if (seen.has(n)) return false; seen.add(n); return true; });
    } catch {
      this.toastr.error('Failed to load pricing tiers.', '', { timeOut: 4000 });
    }
  }

  onTierNameChange() {
    this.versionsForTier = this.allVersions.filter(v => v.name === this.selectedTierName);
    this.selectedVersionId = this.versionsForTier.length ? this.versionsForTier[0].id : null;
    this.selectedVersion = this.versionsForTier.length ? this.versionsForTier[0] : null;
    this.currentItems = [];
    this.cancelRenew();
    if (this.selectedVersionId !== null) this.loadItems(this.selectedVersionId);
  }

  onVersionChange() {
    this.selectedVersion = this.versionsForTier.find(v => v.id === this.selectedVersionId) ?? null;
    this.currentItems = [];
    this.cancelRenew();
    if (this.selectedVersionId !== null) this.loadItems(this.selectedVersionId);
  }

  async loadItems(tierId: number) {
    this.loadingItems = true;
    try {
      this.currentItems = await lastValueFrom(
        this.http.get<PricingTierVersionItem[]>(`/charitycentral/GetPricingTierItemsForVersion?tierId=${tierId}`)
      );
    } catch {
      this.toastr.error('Failed to load pricing items.', '', { timeOut: 4000 });
    } finally {
      this.loadingItems = false;
    }
  }

  isActiveVersion(version: PricingTierVersion | null): boolean {
    return version !== null && version.endDate === null;
  }

  getVersionLabel(version: PricingTierVersion): string {
    const start = this.formatDate(version.startDate);
    const end = version.endDate ? this.formatDate(version.endDate) : '';
    return end ? `${start} - ${end}` : `${start} -`;
  }

  getItemLabel(item: PricingTierVersionItem | RenewItem): string {
    const stLabel = this.serviceTypeLabels[item.serviceTypeId] ?? `Type ${item.serviceTypeId}`;
    const ptLabel = this.pricingTypeLabels[item.pricingType] ?? item.pricingType;
    return `${stLabel} — ${ptLabel}`;
  }

  startRenew() {
    this.renewStartDate = new Date();
    this.renewItems = this.currentItems.map(i => ({
      serviceTypeId: i.serviceTypeId,
      pricingType: i.pricingType,
      cost: null
    }));
    this.isRenewing = true;
  }

  cancelRenew() {
    this.isRenewing = false;
    this.renewItems = [];
  }

  allCostsFilled(): boolean {
    return this.renewItems.length > 0 &&
      this.renewItems.every(i => i.cost !== null && i.cost !== undefined && !isNaN(Number(i.cost)) && Number(i.cost) >= 0);
  }

  async saveRenew() {
    if (!this.allCostsFilled() || this.selectedVersionId === null) return;
    this.saving = true;
    try {
      const request = {
        oldTierId: this.selectedVersionId,
        newStartDate: this.toIsoDate(this.renewStartDate),
        items: this.renewItems.map(i => ({
          serviceTypeId: i.serviceTypeId,
          pricingType: i.pricingType,
          cost: Number(i.cost)
        }))
      };
      await lastValueFrom(this.http.post('/charitycentral/RenewPricingTier', request));
      this.toastr.success('New pricing version saved.', '', { timeOut: 3000 });
      this.cancelRenew();
      // Reload all versions so the new one appears in the dropdown
      this.allVersions = await lastValueFrom(
        this.http.get<PricingTierVersion[]>('/charitycentral/GetAllPricingTierVersions')
      );
      this.versionsForTier = this.allVersions.filter(v => v.name === this.selectedTierName);
      // Select the new active version (EndDate = null)
      const active = this.versionsForTier.find(v => v.endDate === null);
      if (active) {
        this.selectedVersionId = active.id;
        this.selectedVersion = active;
        await this.loadItems(active.id);
      }
    } catch {
      this.toastr.error('Failed to save new pricing version.', '', { timeOut: 4000 });
    } finally {
      this.saving = false;
    }
  }

  formatDate(value: string | Date | null): string {
    if (!value) return '';
    return new Date(value).toLocaleDateString('en-GB');
  }

  private toIsoDate(d: any): string {
    if (typeof d?.format === 'function') return d.format('YYYY-MM-DD');
    const year = (d as Date).getFullYear();
    const month = ((d as Date).getMonth() + 1).toString().padStart(2, '0');
    const day = (d as Date).getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
